/* eslint-disable */
import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppData } from '../../context/AppDataContext';
import { formatCurrency, getCurrentMonth } from '../../utils/helpers';
import { paymentsAPI, expensesAPI, serviceRequestsAPI, tenantsAPI } from '../../services/api';
import { formatMoney, symbolFor, dashboardCapitalByCurrency, vacancyByCurrency, dashboardIncomeProfit } from '../../utils/currencyTotals';
import { PROPERTY_EXPENSE_CATEGORIES } from '../../utils/expenseCategories';

const EXPENSE_CATEGORIES = PROPERTY_EXPENSE_CATEGORIES;


// BUG-005b: the euro cards keep their whole-euro look
const eurWhole = (v) => `€${(v || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;

export default function Dashboard() {
  const { properties, payments, setPayments, expenses, setExpenses, companyExpenses, companies, prospects, calendarTasks, settings } = useAppData();

  // Load tenants and service requests directly — not in AppDataContext
  const [tenants, setTenants] = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);
  useEffect(() => {
    tenantsAPI.getAll().then(setTenants).catch(() => {});
    serviceRequestsAPI.getAll().then(setServiceRequests).catch(() => {});
  }, []);
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState({});
  const [payModal, setPayModal] = useState(null); // { propertyId, roomId, rentAmount, tenantName, propertyAddress, roomName }
  const [payForm, setPayForm] = useState({ month: '', amount: '', paidDate: '' });
  const [payLoading, setPayLoading] = useState(false);
  const [payError, setPayError] = useState('');
  const [paymentWarning, setPaymentWarning] = useState(null);
  const [addExpenseAfterPayment, setAddExpenseAfterPayment] = useState(false);
  const [quickExpenses, setQuickExpenses] = useState([{ category:'', amount:'', description:'', paid:true }]);
  const addQuickExpenseRow = () => setQuickExpenses(p => [...p, { category:'', amount:'', description:'', paid:true }]);
  const removeQuickExpenseRow = (i) => setQuickExpenses(p => p.filter((_,j) => j !== i));
  const setQuickExpenseField = (i, field, value) => setQuickExpenses(p => p.map((row,j) => j===i ? {...row,[field]:value} : row));
  const toggle = (id) => setCollapsed(p => ({ ...p, [id]: !p[id] }));

  const today = new Date();
  const currentYear = today.getFullYear();
  const ytdStart = new Date(currentYear, 0, 1);
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const currentMonthLabel = today.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  const ytdLabel = `YTD ${currentYear}`;

  // Only active (non-archived) properties
  const activeProps = properties.filter(p => !p.archived);
  const gbpProps = activeProps.filter(p => (p.currency || 'GBP') === 'GBP');
  const eurProps = activeProps.filter(p => p.currency === 'EUR');
  const personalProps = activeProps.filter(p => p.ownership === 'personal');
  const companyProps = activeProps.filter(p => p.ownership === 'company');
  // BUG-010: jointly owned properties are counted and shown on their own lines (100% figures), not mixed into personal
  const jointProps = activeProps.filter(p => String(p.ownership || '').toLowerCase() === 'joint');

  const inRange = (dateStr, start) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    return d >= start && d <= today;
  };

  const stats = useMemo(() => {
    // Occupancy
    let totalRooms = 0, occupiedRooms = 0;
    const vacantRooms = [];
    activeProps.forEach(prop => {
      const isPropHMO = String(prop.property_type||prop.propertyType||'hmo').toLowerCase() === 'hmo';
      if (isPropHMO) {
        const rooms = prop.rooms || [];
        totalRooms += rooms.length;
        rooms.forEach(room => {
          if (room.status === 'occupied') { occupiedRooms++; }
          else {
            const days = room.vacantSince ? Math.floor((today - new Date(room.vacantSince)) / 86400000) : 0;
            vacantRooms.push({ ...room, propertyAddress: prop.address, propertyId: prop.id, daysVacant: days, currency: prop.currency || 'GBP' });
          }
        });
      } else {
        // BUG FIX: this only ever iterated prop.rooms, so a vacant
        // BTL/Single Let property (no rooms by design) never appeared in
        // the Vacancy Impact widget at all — no alert that it was sitting
        // empty and losing money. Counted as one "room" for occupancy
        // purposes (a non-HMO property is one unit). If vacant, the most
        // recent past tenancy (by move_out_date/lease_end) provides a
        // rentAmount estimate and a vacantSince date for the days-vacant
        // count — if there's no past tenancy either (never let), it still
        // appears in the list, just without a lost-income estimate.
        totalRooms += 1;
        const activeTenant = (tenants||[]).find(t => String(t.property_id||t.propertyId)===String(prop.id) && t.status==='active');
        if (activeTenant) {
          occupiedRooms++;
        } else {
          const pastTenancies = (tenants||[]).filter(t => String(t.property_id||t.propertyId)===String(prop.id) && (t.move_out_date || t.lease_end));
          const mostRecent = pastTenancies.sort((a,b) => {
            const aEnd = a.move_out_date || a.lease_end, bEnd = b.move_out_date || b.lease_end;
            return String(bEnd).localeCompare(String(aEnd));
          })[0];
          const vacantSince = mostRecent ? String(mostRecent.move_out_date || mostRecent.lease_end).substring(0,10) : null;
          const days = vacantSince ? Math.floor((today - new Date(vacantSince)) / 86400000) : 0;
          vacantRooms.push({
            id: '', roomName: 'Full Property', status: 'vacant',
            rentAmount: mostRecent ? parseFloat(mostRecent.rent_amount||0) : 0,
            vacantSince, propertyAddress: prop.address, propertyId: prop.id, daysVacant: days, currency: prop.currency || 'GBP',
          });
        }
      }
    });
    const occupancy = totalRooms > 0 ? (occupiedRooms / totalRooms) * 100 : 0;

    // BUG-005b: income and profit are worked out one currency at a time (euro and pound amounts are never added
    // together, and nothing is converted). Company overheads reduce only the profit of their own company's currency.
    const paidDateOf = (p) => p.paidDate || p.paid_date;
    const incomeProfit = (currency, start) => dashboardIncomeProfit({ properties: activeProps, payments, expenses, companyExpenses, companies, currency, start, end: today, paidDateOf });
    const gbpYTD = incomeProfit('GBP', ytdStart), gbpMo = incomeProfit('GBP', monthStart);
    const eurYTD = incomeProfit('EUR', ytdStart), eurMo = incomeProfit('EUR', monthStart);

    // Capital metrics, one block per currency (no exchange rate and no conversion)
    const capitalGroups = dashboardCapitalByCurrency(activeProps);
    const propsWithData = capitalGroups.reduce((n, g) => n + g.count, 0);

    // Overdue rents (occupied rooms where this month has no payment)
    const currentMonth = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}`;
    // BUG FIX: This used to only check whether THIS month's rent was paid —
    // if a tenant paid the current month but had an earlier missed month, or
    // was several months behind and caught up on just one, they'd
    // incorrectly disappear from Overdue Rent entirely. Same root cause and
    // fix as the Arrears Report: check every expected month individually
    // against actual payments, not just the most recent one.
    const monthsBetween = (start, end) => {
      const out = [];
      let [sy, sm] = start.split('-').map(Number);
      const [ey, em] = end.split('-').map(Number);
      let guard = 0;
      while ((sy < ey || (sy === ey && sm <= em)) && guard < 60) {
        out.push(`${sy}-${String(sm).padStart(2,'0')}`);
        sm++; if (sm > 12) { sm = 1; sy++; }
        guard++;
      }
      return out;
    };

    // Still needed below by overdueFromTenants — that check is already
    // correctly bounded to one tenant's own real period (no earlier
    // tenant's data involved, so no void-gap risk there), but still needs
    // a sensible floor when lease_start itself is missing.
    const resolveCheckFrom = (leaseStart, recordPayments, upToMonth) => {
      if (leaseStart) return leaseStart;
      const earliestPayment = recordPayments.reduce((min, p) => (p.month && (!min || p.month < min)) ? p.month : min, null);
      return earliestPayment || upToMonth;
    };
    // BUG FIX (BACKDATE-003): This used to check a single CONTINUOUS range
    // from one lease_start to the present — meaning a genuine void month
    // between two tenancies (nobody living there, nothing owed) was
    // incorrectly flagged as overdue. Fixed the same way as the Arrears
    // Report: derive expected months from the ACTUAL UNION of every real
    // tenancy period recorded for the room, so a month nobody was
    // tenanted for is correctly never "expected" in the first place.
    const overdueRents = [];
    activeProps.forEach(prop => {
      (prop.rooms || []).filter(r => r.status === 'occupied').forEach(room => {
        // Same fix as the Arrears Report — includes any tenant with SOME
        // date signal (not just lease_start), inferring a missing start
        // from whoever occupied the room immediately before them where a
        // predecessor exists to infer from.
        const roomTenancies = (tenants || []).filter(t =>
          String(t.room_id) === String(room.id) && String(t.property_id || t.propertyId) === String(prop.id) &&
          (t.lease_start || t.lease_end || t.move_out_date)
        );
        if (!roomTenancies.length) return; // no tenancy history with any usable date at all
        const withRef = roomTenancies.map(t => {
          const ends = [t.lease_end, t.move_out_date].filter(Boolean).map(d => String(d).substring(0,7));
          return { t, ref: ends.length ? ends.sort()[0] : String(t.lease_start).substring(0,7) };
        }).sort((a,b) => a.ref.localeCompare(b.ref));
        const monthToTenancy = {};
        withRef.forEach(({ t }, idx) => {
          let start = t.lease_start ? String(t.lease_start).substring(0,7) : null;
          if (!start) {
            const predecessor = withRef[idx - 1];
            if (!predecessor) return; // room's first known tenant, no signal to infer a start from
            const predEnds = [predecessor.t.lease_end, predecessor.t.move_out_date].filter(Boolean).map(d=>String(d).substring(0,7));
            start = predEnds.length ? predEnds.sort()[0] : null;
            if (!start) return;
          }
          // BUG FIX: same gap as the Arrears Report — only read lease_end,
          // never move_out_date. The "Move Out" action sets move_out_date,
          // not lease_end, so a genuinely moved-out tenant with no
          // lease_end set (a normal, common state) was still treated as
          // occupying the room right up to the current month, showing them
          // as overdue on rent they'd never be charged for after leaving.
          const candidateEnds = [t.lease_end, t.move_out_date].filter(Boolean).map(d => String(d).substring(0,7));
          const endRaw = candidateEnds.length ? candidateEnds.sort()[0] : currentMonth;
          const end = endRaw < currentMonth ? endRaw : currentMonth;
          if (start > end) return;
          monthsBetween(start, end).forEach(m => { monthToTenancy[m] = t; });
        });
        const roomPayments = payments.filter(p => (p.propertyId || p.property_id) == prop.id && (p.roomId || p.room_id) === room.id);
        const paidMonthsSet = new Set(roomPayments.map(p => p.month));
        // BUG FIX: this used to gate on a single ROOM-level due day
        // (room.paymentDueDay) applied uniformly to every tenancy sharing
        // that room — so a brand new tenant with a later personal due date
        // (e.g. a prorated first payment due the 28th) was flagged overdue
        // the moment the room's generic day passed, regardless of her own
        // actual due date. Every past month is unambiguous either way — a
        // full calendar month has elapsed. Only the current month needs a
        // due-date check, and it's now judged against the RESPONSIBLE
        // tenant's own payment_due_day, falling back to the room's setting
        // only if that tenant has no personal due day recorded.
        const missingMonths = Object.keys(monthToTenancy).filter(m => {
          if (paidMonthsSet.has(m)) return false;
          if (m === currentMonth) {
            const responsibleTenant = monthToTenancy[m];
            const dueDay = parseInt(responsibleTenant.payment_due_day || room.paymentDueDay || 1);
            const dueDateThisMonth = new Date(today.getFullYear(), today.getMonth(), dueDay);
            if (dueDateThisMonth >= today) return false;
          }
          return true;
        }).sort();
        if (missingMonths.length > 0) {
          // Days overdue based on the OLDEST missing month's due date, so
          // this correctly escalates the longer rent stays unpaid rather
          // than resetting whenever the current month happens to be paid.
          // Uses the SAME per-tenant due day as the filter above, rather
          // than a room-level default, so the countdown matches whichever
          // date actually applies to the tenant responsible for it.
          const [oy, om] = missingMonths[0].split('-').map(Number);
          const oldestResponsibleTenant = monthToTenancy[missingMonths[0]];
          const oldestDueDay = parseInt(oldestResponsibleTenant?.payment_due_day || room.paymentDueDay || 1);
          const oldestDueDate = new Date(oy, om-1, oldestDueDay);
          const daysOverdue = Math.floor((today - oldestDueDate) / 86400000);
          overdueRents.push({ ...room, currency: prop.currency || 'GBP', propertyAddress: prop.address, propertyId: prop.id, dueDate: oldestDueDate, daysOverdue, monthsOverdue: missingMonths.length });
        }
      });
    });

    // Tenant stats
    const activeTenants   = (tenants || []).filter(t => t.status === 'active');
    const expiringLeases  = activeTenants.filter(t => {
      if (!t.lease_end) return false;
      const end = new Date(t.lease_end.substring(0,10) + 'T12:00:00');
      const soon = new Date(); soon.setDate(soon.getDate() + 60);
      return end >= today && end <= soon;
    });

    // Rent due this week from tenants
    const weekEnd = new Date(); weekEnd.setDate(weekEnd.getDate() + 7);
    const rentDueThisWeek = activeTenants.filter(t => {
      if (!t.payment_due_day) return false;
      const dueDay = parseInt(t.payment_due_day);
      const dueDate = new Date(today.getFullYear(), today.getMonth(), dueDay);
      return dueDate >= today && dueDate <= weekEnd;
    });

    // Overdue rent from tenants (BTL + HMO via tenant records)
    // BUG FIX: this only ever filtered — once a tenant was determined
    // overdue, every detail of that calculation (which month, how many
    // days) was discarded. The widget below had a "days overdue" figure
    // for room-only fallback rows, but nothing at all for the much more
    // common tenant-linked rows — exactly the gap Sam reported. Now
    // computes the same oldest-missing-month due date already used for
    // the room-based calculation above, and attaches it to each qualifying
    // tenant instead of throwing it away.
    const overdueFromTenants = activeTenants.reduce((acc, t) => {
      if (!t.payment_due_day) return acc;
      const dueDay = parseInt(t.payment_due_day);
      const dueDateThisMonth = new Date(today.getFullYear(), today.getMonth(), dueDay);
      const upToMonth = dueDateThisMonth < today ? currentMonth : new Date(today.getFullYear(), today.getMonth()-1, 1).toISOString().substring(0,7);
      const leaseStart = t.lease_start ? String(t.lease_start).substring(0,7) : null;
      const isHMO = (t.property_type || t.property_type_snapshot) === 'hmo';
      const tenantPayments = payments.filter(p => {
        const propMatch = String(p.property_id || p.propertyId) === String(t.property_id);
        const roomMatch = isHMO ? String(p.room_id || p.roomId) === String(t.room_id) : true;
        return propMatch && roomMatch;
      });
      const checkFrom = resolveCheckFrom(leaseStart, tenantPayments, upToMonth);
      if (checkFrom > upToMonth) return acc; // lease starts after the window we're checking
      const expectedMonths = monthsBetween(checkFrom, upToMonth);
      const paidMonthsSet = new Set(tenantPayments.map(p => p.month));
      const missingMonths = expectedMonths.filter(m => !paidMonthsSet.has(m));
      if (!missingMonths.length) return acc;
      const [oy, om] = missingMonths[0].split('-').map(Number);
      const oldestDueDate = new Date(oy, om-1, dueDay);
      const daysOverdue = Math.floor((today - oldestDueDate) / 86400000);
      acc.push({ ...t, dueDate: oldestDueDate, daysOverdue, monthsOverdue: missingMonths.length });
      return acc;
    }, []);

    // BUG FIX: had no upper bound at all — just "not completed and due
    // today or later," sorted, top 5. A December item could easily land
    // in that top 5 if there weren't 5 closer tasks to push it out,
    // which is exactly what Sam saw. Now capped to the next 30 days.
    const todayStr = today.toISOString().split('T')[0];
    const thirtyDaysOut = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const upcomingTasks = calendarTasks
      .filter(t => !t.completed && (t.due_date || t.date) >= todayStr && (t.due_date || t.date) <= thirtyDaysOut)
      .sort((a, b) => new Date(a.due_date || a.date) - new Date(b.due_date || b.date))
      .slice(0, 5);

    return {
      totalProperties: activeProps.length, totalRooms, occupiedRooms, occupancy, vacantRooms, overdueRents, upcomingTasks,
      activeTenants: activeTenants.length, expiringLeases: expiringLeases.length,
      rentDueThisWeek: rentDueThisWeek.length, overdueFromTenants,
      gbpYTD, gbpMo, eurYTD, eurMo,
      capitalGroups, propsWithData, vacancyGroups: vacancyByCurrency(vacantRooms),
    };
  }, [activeProps, payments, expenses, companyExpenses, companies, calendarTasks, tenants]);

  const Section = ({ id, title, children, defaultOpen = true }) => (
    <div className="dashboard-section">
      <div className="accordion-header" onClick={() => toggle(id)}>
        <span className="section-title">{title}</span>
        <i className={`ti ${collapsed[id] ? 'ti-chevron-right' : 'ti-chevron-down'}`} style={{ fontSize: '1rem', color: 'var(--text-secondary)' }} aria-hidden="true" />
      </div>
      {!collapsed[id] && <div style={{ marginTop: '1rem' }}>{children}</div>}
    </div>
  );

  const StatCard = ({ icon, label, value, color = 'var(--text-primary)', sub, accent }) => (
    <div className={`stat-card${accent ? ' accent-' + accent : ''}`}>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color, fontSize: '1.375rem' }}>{value}</div>
      {sub && <div className="stat-change">{sub}</div>}
    </div>
  );

  const openPayModal = (r) => {
    const today = new Date().toISOString().split('T')[0];
    setPayForm({ month: getCurrentMonth(), amount: String(r.rentAmount || ''), paidDate: today });
    setPayError('');
    setPayModal(r);
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!payModal) return;
    setPayLoading(true); setPayError('');
    try {
      const result = await paymentsAPI.create({
        propertyId: payModal.propertyId,
        roomId: payModal.id || payModal.roomId,
        month: payForm.month,
        amount: parseFloat(payForm.amount),
        paidDate: payForm.paidDate
      });
      setPayments(prev => [result, ...prev]);
      // DUPE-001: Non-blocking heads-up — see PropertyDetail.js for full rationale.
      if (result.duplicateWarning) {
        const dw = result.duplicateWarning;
        setPaymentWarning(
          `⚠️ Heads up — this is payment #${dw.priorPaymentCount + 1} recorded for this room this month (previous total ${symbolFor(payModal.currency)}${dw.priorTotalAmount.toFixed(2)}, now ${symbolFor(payModal.currency)}${dw.newTotalAmount.toFixed(2)}). If this was a split payment that's fine — if it was accidental, you can delete the duplicate from Reports → Data Export or the property's Payments tab.`
        );
        setTimeout(() => setPaymentWarning(null), 12000);
      }
      if (addExpenseAfterPayment) {
        for (const qe of quickExpenses) {
          if (qe.category && qe.amount) {
            try {
              const expResult = await expensesAPI.create({
                propertyId: payModal.propertyId,
                category: qe.category,
                amount: parseFloat(qe.amount),
                date: payForm.paidDate,
                description: qe.description || '',
                paid: qe.paid
              });
              setExpenses(prev => [expResult, ...prev]);
            } catch (expErr) { console.warn('Quick expense failed:', expErr); }
          }
        }
      }
      setPayModal(null);
      setAddExpenseAfterPayment(false);
      setQuickExpenses([{ category:'', amount:'', description:'', paid:true }]);
    } catch (err) {
      setPayError(err.error || 'Failed to record payment. Please try again.');
    } finally { setPayLoading(false); }
  };

  const oc = stats.occupancy;
  const ocColor = oc >= 90 ? '#15803d' : oc >= 70 ? '#d97706' : '#b91c1c';

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">Dashboard</h1>
          <span className="page-subtitle">{today.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
        </div>
      </div>

      {paymentWarning && (
        <div style={{ background:'#fef3c7', border:'1px solid #fde047', borderRadius:'0.5rem', padding:'0.85rem 1rem', marginBottom:'1rem', fontSize:'0.875rem', color:'#92400e', display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'1rem' }}>
          <span>{paymentWarning}</span>
          <button onClick={() => setPaymentWarning(null)} style={{ background:'none', border:'none', color:'#92400e', cursor:'pointer', fontSize:'1rem', flexShrink:0, lineHeight:1 }}>×</button>
        </div>
      )}

      {/* Overdue alerts */}
      {/* ── Quick stats row ── */}
      <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
        <StatCard icon="👤" label="Active Tenants" value={stats.activeTenants}
          color="#1e40af"
          sub={stats.expiringLeases > 0
            ? `⚠️ ${stats.expiringLeases} lease${stats.expiringLeases !== 1 ? 's' : ''} expiring in 60 days`
            : `${stats.occupiedRooms} of ${stats.totalRooms} rooms occupied`}
        />
        <StatCard icon="💰" label="Rent Due This Week" value={stats.rentDueThisWeek}
          color={stats.rentDueThisWeek > 0 ? '#d97706' : '#15803d'}
          sub={stats.rentDueThisWeek > 0 ? 'Payments expected' : 'None due this week'}
        />
        <StatCard icon="⚠️" label="Overdue Rent" value={stats.overdueFromTenants.length}
          color={stats.overdueFromTenants.length > 0 ? '#dc2626' : '#15803d'}
          sub={stats.overdueFromTenants.length > 0 ? 'Tenants not paid this month' : 'All rent up to date'}
        />
        <StatCard icon="🔧" label="Active Requests" value={serviceRequests.filter(r => !['completed','closed','cancelled'].includes(r.status)).length}
          color={serviceRequests.some(r => ['emergency','high'].includes(r.priority) && !['completed','closed','cancelled'].includes(r.status)) ? '#dc2626' : '#475569'}
          sub={serviceRequests.some(r => r.priority === 'emergency' && !['completed','closed','cancelled'].includes(r.status)) ? '🚨 Emergency request open' : serviceRequests.some(r => r.status === 'quote_received') ? '💬 Quote awaiting review' : 'No urgent issues'}
        />
      </div>

      {/* ── Unified Overdue Rent widget ── */}
      {(stats.overdueFromTenants.length > 0 || stats.overdueRents.length > 0) && (() => {
        // Build unified list — prefer tenant records (have View Tenant), fall back to room records
        const tenantIds = new Set(stats.overdueFromTenants.map(t => `${t.property_id}-${t.room_id}`));
        const roomOnlyOverdue = stats.overdueRents.filter(r => !tenantIds.has(`${r.propertyId}-${r.id}`));
        return (
          <div className="card" style={{ marginBottom: '1.5rem', borderLeft: '4px solid #dc2626' }}>
            <div style={{ fontWeight: 700, marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#dc2626' }}>
              <i className="ti ti-alert-triangle" aria-hidden="true" />
              Overdue Rent — {stats.overdueFromTenants.length + roomOnlyOverdue.length} tenant{stats.overdueFromTenants.length + roomOnlyOverdue.length !== 1 ? 's' : ''}
            </div>
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {/* Tenant-based overdue rows */}
              {stats.overdueFromTenants.map((t, i) => {
                const sym = symbolFor(t.currency);
                // Find matching room for Record Payment
                const prop = activeProps.find(p => String(p.id) === String(t.property_id));
                const room = prop?.rooms?.find(r => String(r.id) === String(t.room_id));
                return (
                  <div key={t.id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.75rem', background: 'var(--bg-light)', borderRadius: '0.375rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{t.first_name} {t.last_name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        {t.property_address}{room ? ` · ${room.roomName}` : ''}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600, marginTop: '0.125rem' }}>
                        Due {t.dueDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} · {t.daysOverdue} day{t.daysOverdue !== 1 ? 's' : ''} overdue{t.monthsOverdue > 1 ? ` (${t.monthsOverdue} months behind)` : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {t.rent_amount && <span style={{ fontWeight: 700, color: '#dc2626' }}>{sym}{parseFloat(t.rent_amount).toFixed(2)}/mo</span>}
                      {room && <button className="btn btn-success btn-sm" onClick={() => openPayModal({ ...room, currency: t.currency, propertyAddress: t.property_address, propertyId: t.property_id })}>💰 Record Payment</button>}
                      <button className="btn btn-secondary btn-sm" onClick={() => navigate('/tenants/' + t.id)}>👤 View Tenant</button>
                    </div>
                  </div>
                );
              })}
              {/* Room-only overdue rows (HMO rooms without linked tenant record) */}
              {roomOnlyOverdue.map((r, i) => (
                <div key={`room-${i}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.75rem', background: 'var(--bg-light)', borderRadius: '0.375rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{r.roomName} {r.tenantName ? `— ${r.tenantName}` : ''}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{r.propertyAddress} · {r.daysOverdue} day{r.daysOverdue !== 1 ? 's' : ''} overdue</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <strong>{formatMoney(r.rentAmount, r.currency)}</strong>
                    <button className="btn btn-success btn-sm" onClick={() => openPayModal(r)}>💰 Record Payment</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Portfolio Overview */}
      <Section id="portfolio" title="📊 Portfolio Overview">
        <div className="stats-grid">
          <StatCard icon="🏢" label="Personal Properties" value={personalProps.length} color="#1e40af" />
          <StatCard icon="🏘️" label="Company Properties" value={companyProps.length} color="#6b21a8" />
          <StatCard icon="🤝" label="Joint Properties" value={jointProps.length} color="#0f766e" />
          <StatCard icon="📊" label="Portfolio Occupancy" value={`${oc.toFixed(1)}%`} color={ocColor} sub={`${stats.occupiedRooms}/${stats.totalRooms} rooms`} />
          <StatCard icon="🔑" label="Vacant Rooms" value={stats.vacantRooms.length} color={stats.vacantRooms.length > 0 ? '#dc2626' : '#15803d'} />
        </div>
      </Section>

      {/* GBP Income */}
      {gbpProps.length > 0 && (
        <Section id="gbp" title={`💷 GBP Income & Profit (${gbpProps.length} properties)`} defaultOpen={false}>
          <div style={{ marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{ytdLabel}</div>
          <div className="stats-grid" style={{ marginBottom: '1rem' }}>
            <StatCard icon="💷" label={`Personal Income (${ytdLabel})`} value={formatMoney(stats.gbpYTD.personalInc, 'GBP')} color="#15803d" />
            <StatCard icon="💷" label={`Company Income (${ytdLabel})`} value={formatMoney(stats.gbpYTD.companyInc, 'GBP')} color="#15803d" />
            <StatCard icon="💰" label={`Personal Net Profit (${ytdLabel})`} value={formatMoney(stats.gbpYTD.personalProfit, 'GBP')} color={stats.gbpYTD.personalProfit >= 0 ? '#0f766e' : '#b91c1c'} />
            <StatCard icon="💰" label={`Company Net Profit (${ytdLabel})`} value={formatMoney(stats.gbpYTD.companyProfit, 'GBP')} color={stats.gbpYTD.companyProfit >= 0 ? '#0f766e' : '#b91c1c'} />
            {stats.gbpYTD.jointCount > 0 && <StatCard icon="🤝" label={`Joint Properties Income (${ytdLabel}, 100%)`} value={formatMoney(stats.gbpYTD.jointInc, 'GBP')} color="#15803d" sub={`${stats.gbpYTD.jointCount} joint propert${stats.gbpYTD.jointCount === 1 ? 'y' : 'ies'}`} />}
            {stats.gbpYTD.jointCount > 0 && <StatCard icon="🤝" label={`Joint Properties Net Profit (${ytdLabel}, 100%)`} value={formatMoney(stats.gbpYTD.jointProfit, 'GBP')} color={stats.gbpYTD.jointProfit >= 0 ? '#0f766e' : '#b91c1c'} />}
          </div>
          <div style={{ marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{currentMonthLabel}</div>
          <div className="stats-grid">
            <StatCard icon="💷" label={`Personal Income (${currentMonthLabel})`} value={formatMoney(stats.gbpMo.personalInc, 'GBP')} color="#1e40af" />
            <StatCard icon="💷" label={`Company Income (${currentMonthLabel})`} value={formatMoney(stats.gbpMo.companyInc, 'GBP')} color="#1e40af" />
            <StatCard icon="💰" label={`Personal Net Profit (${currentMonthLabel})`} value={formatMoney(stats.gbpMo.personalProfit, 'GBP')} color={stats.gbpMo.personalProfit >= 0 ? '#6b21a8' : '#b91c1c'} />
            <StatCard icon="💰" label={`Company Net Profit (${currentMonthLabel})`} value={formatMoney(stats.gbpMo.companyProfit, 'GBP')} color={stats.gbpMo.companyProfit >= 0 ? '#6b21a8' : '#b91c1c'} />
            {stats.gbpMo.jointCount > 0 && <StatCard icon="🤝" label={`Joint Properties Income (${currentMonthLabel}, 100%)`} value={formatMoney(stats.gbpMo.jointInc, 'GBP')} color="#1e40af" />}
            {stats.gbpMo.jointCount > 0 && <StatCard icon="🤝" label={`Joint Properties Net Profit (${currentMonthLabel}, 100%)`} value={formatMoney(stats.gbpMo.jointProfit, 'GBP')} color={stats.gbpMo.jointProfit >= 0 ? '#6b21a8' : '#b91c1c'} />}
          </div>
          {stats.gbpMo.jointCount > 0 && <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>Joint properties are shown at 100% on their own lines and are not included in the personal figures. Your ownership share is set on each property.</div>}
        </Section>
      )}

      {/* EUR Income */}
      {eurProps.length > 0 && (
        <Section id="eur" title={`💶 EUR Income & Profit (${eurProps.length} properties)`} defaultOpen={false}>
          <div style={{ marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{ytdLabel}</div>
          <div className="stats-grid" style={{ marginBottom: '1rem' }}>
            <StatCard icon="💶" label={`Personal Income (${ytdLabel})`} value={eurWhole(stats.eurYTD.personalInc)} color="#15803d" />
            <StatCard icon="💶" label={`Company Income (${ytdLabel})`} value={eurWhole(stats.eurYTD.companyInc)} color="#15803d" />
            <StatCard icon="💰" label={`Personal Net Profit (${ytdLabel})`} value={eurWhole(stats.eurYTD.personalProfit)} color={stats.eurYTD.personalProfit >= 0 ? '#0f766e' : '#b91c1c'} />
            <StatCard icon="💰" label={`Company Net Profit (${ytdLabel})`} value={eurWhole(stats.eurYTD.companyProfit)} color={stats.eurYTD.companyProfit >= 0 ? '#0f766e' : '#b91c1c'} />
            {stats.eurYTD.jointCount > 0 && <StatCard icon="🤝" label={`Joint Properties Income (${ytdLabel}, 100%)`} value={eurWhole(stats.eurYTD.jointInc)} color="#15803d" sub={`${stats.eurYTD.jointCount} joint propert${stats.eurYTD.jointCount === 1 ? 'y' : 'ies'}`} />}
            {stats.eurYTD.jointCount > 0 && <StatCard icon="🤝" label={`Joint Properties Net Profit (${ytdLabel}, 100%)`} value={eurWhole(stats.eurYTD.jointProfit)} color={stats.eurYTD.jointProfit >= 0 ? '#0f766e' : '#b91c1c'} />}
          </div>
          <div style={{ marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{currentMonthLabel}</div>
          <div className="stats-grid">
            <StatCard icon="💶" label={`Personal Income (${currentMonthLabel})`} value={eurWhole(stats.eurMo.personalInc)} color="#1e40af" />
            <StatCard icon="💶" label={`Company Income (${currentMonthLabel})`} value={eurWhole(stats.eurMo.companyInc)} color="#1e40af" />
            <StatCard icon="💰" label={`Personal Net Profit (${currentMonthLabel})`} value={eurWhole(stats.eurMo.personalProfit)} color={stats.eurMo.personalProfit >= 0 ? '#6b21a8' : '#b91c1c'} />
            <StatCard icon="💰" label={`Company Net Profit (${currentMonthLabel})`} value={eurWhole(stats.eurMo.companyProfit)} color={stats.eurMo.companyProfit >= 0 ? '#6b21a8' : '#b91c1c'} />
            {stats.eurMo.jointCount > 0 && <StatCard icon="🤝" label={`Joint Properties Income (${currentMonthLabel}, 100%)`} value={eurWhole(stats.eurMo.jointInc)} color="#1e40af" />}
            {stats.eurMo.jointCount > 0 && <StatCard icon="🤝" label={`Joint Properties Net Profit (${currentMonthLabel}, 100%)`} value={eurWhole(stats.eurMo.jointProfit)} color={stats.eurMo.jointProfit >= 0 ? '#6b21a8' : '#b91c1c'} />}
          </div>
          {stats.eurMo.jointCount > 0 && <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>Joint properties are shown at 100% on their own lines and are not included in the personal figures. Your ownership share is set on each property.</div>}
        </Section>
      )}

      {/* Capital Performance: one block per currency, never added together, never converted */}
      {stats.propsWithData > 0 && (
        <Section id="capital" title="💎 Capital Performance" defaultOpen={false}>
          {stats.capitalGroups.length > 1 && <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>Each currency is totalled on its own. Amounts in different currencies are never added together or converted.</div>}
          {stats.capitalGroups.map(g => {
            const multi = stats.capitalGroups.length > 1;
            const tag = multi ? ` (${g.currency})` : '';
            return (<div key={g.currency} style={{ marginBottom: multi ? '1rem' : 0 }}>
              {multi && <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>{g.currency} ({g.symbol})</div>}
              <div className="stats-grid">
                <StatCard icon="💎" label={`Total Equity${tag}`} value={formatMoney(g.equity, g.currency)} color="#15803d" />
                <StatCard icon="🏦" label={`Total Mortgage Balance${tag}`} value={formatMoney(g.mortgage, g.currency)} color="#dc2626" />
                <StatCard icon="📊" label={`Average Portfolio LTV${tag}`} value={`${g.ltv.toFixed(1)}%`} color={g.ltv > 75 ? '#dc2626' : g.ltv > 60 ? '#d97706' : '#15803d'} />
                <StatCard icon="🏠" label={`Total Portfolio Value${tag}`} value={formatMoney(g.value, g.currency)} color="#1e40af" />
              </div>
            </div>);
          })}
        </Section>
      )}

      {/* Vacancy Impact */}
      {stats.vacantRooms.length > 0 && (
        <Section id="vacancy" title={`🔑 Vacancy Impact Report (${stats.vacantRooms.length} rooms)`} defaultOpen={false}>
          {stats.vacancyGroups.length > 1 && <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>Each currency is totalled on its own. Amounts in different currencies are never added together or converted.</div>}
          {stats.vacancyGroups.map(g => {
            const tag = stats.vacancyGroups.length > 1 ? ` (${g.currency})` : '';
            return (<div key={g.currency} className="stats-grid" style={{ marginBottom: '1rem' }}>
              <StatCard icon="📉" label={`Monthly Lost Income${tag}`} value={formatMoney(g.monthlyLost, g.currency)} color="#d97706" />
              <StatCard icon="📅" label={`Annual Projection (if stays vacant)${tag}`} value={formatMoney(g.annualProjection, g.currency)} color="#b91c1c" />
            </div>);
          })}
          <div className="stats-grid" style={{ marginBottom: '1rem' }}>
            <StatCard icon="⏱️" label="Avg Days Vacant" value={`${stats.vacantRooms.length > 0 ? Math.round(stats.vacantRooms.reduce((s,r)=>s+r.daysVacant,0)/stats.vacantRooms.length) : 0} days`} color="#6b7280" />
          </div>
          <div className="card">
            <div className="table-container">
              <table className="table">
                <thead><tr><th>Property</th><th>Room</th><th>Vacant Since</th><th>Days Vacant</th><th>Monthly Lost</th></tr></thead>
                <tbody>
                  {stats.vacantRooms.map((r, i) => (
                    <tr key={i}>
                      <td>{r.propertyAddress}</td>
                      <td>{r.roomName}</td>
                      <td>{r.vacantSince ? new Date(r.vacantSince).toLocaleDateString('en-GB') : 'Unknown'}</td>
                      <td><span className={`badge ${r.daysVacant > 30 ? 'badge-danger' : 'badge-warning'}`}>{r.daysVacant} days</span></td>
                      <td>{formatMoney(r.rentAmount, r.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Section>
      )}

      {/* Properties Overview */}
      <Section id="properties" title={`🏠 Properties Overview (${activeProps.length})`}>
        {activeProps.length === 0 ? (
          <div className="empty-state"><span>🏠</span><h3>No Properties Yet</h3><p>Add your first property to get started</p><button className="btn btn-primary" onClick={() => navigate('/properties')}>Add Property</button></div>
        ) : (
          <div className="property-grid">
            {activeProps.map(prop => {
              const rooms = prop.rooms || [];
              const occupied = rooms.filter(r => r.status === 'occupied').length;
              const occPct = rooms.length > 0 ? (occupied / rooms.length) * 100 : 0;
              const barColor = occPct >= 90 ? '#15803d' : occPct >= 70 ? '#d97706' : '#b91c1c';
              const propPayments = payments.filter(p => (p.propertyId||p.property_id) == prop.id).reduce((s,p)=>s+parseFloat(p.amount||0),0);
              const propExpenses = expenses.filter(e => (e.paid===true||e.paid==='true') && (e.propertyId||e.property_id)==prop.id).reduce((s,e)=>s+parseFloat(e.amount||0),0);
              const profit = propPayments - propExpenses;
              const symbol = symbolFor(prop.currency);
              return (
                <div key={prop.id} className="property-card" onClick={() => navigate('/properties')}>
                  <div className="property-card-header">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.9375rem', lineHeight: 1.3 }}>{prop.address}</div>
                        <div style={{ fontSize: '0.75rem', opacity: 0.85, marginTop: '0.2rem' }}>{prop.ownership} • {prop.currency || 'GBP'}</div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.25rem', flexShrink: 0 }}>
                        <span style={{ background: 'rgba(255,255,255,0.2)', padding: '0.2rem 0.4rem', borderRadius: '0.25rem', fontSize: '0.7rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{rooms.length} rooms</span>
                        {(() => {
                          const pt = prop.property_type || prop.propertyType || '';
                          const typeMap = {
                            'hmo': { label: 'HMO', bg: '#1e40af', color: '#fff' },
                            'HMO': { label: 'HMO', bg: '#1e40af', color: '#fff' },
                            'btl': { label: 'BTL', bg: '#065f46', color: '#fff' },
                            'BTL': { label: 'BTL', bg: '#065f46', color: '#fff' },
                            'buy-to-let': { label: 'BTL', bg: '#065f46', color: '#fff' },
                            'Buy to Let': { label: 'BTL', bg: '#065f46', color: '#fff' },
                            'single-let': { label: 'Single Let', bg: '#065f46', color: '#fff' },
                            'Single Let': { label: 'Single Let', bg: '#065f46', color: '#fff' },
                            'commercial': { label: 'Commercial', bg: '#78350f', color: '#fff' },
                            'Commercial': { label: 'Commercial', bg: '#78350f', color: '#fff' },
                            'holiday-let': { label: 'Holiday Let', bg: '#6b21a8', color: '#fff' },
                            'Holiday Let': { label: 'Holiday Let', bg: '#6b21a8', color: '#fff' },
                          };
                          const t = typeMap[pt];
                          if (!t) return null;
                          return <span style={{ background: t.bg, color: t.color, padding: '0.15rem 0.4rem', borderRadius: '0.25rem', fontSize: '0.65rem', fontWeight: 700, whiteSpace: 'nowrap' }}>{t.label}</span>;
                        })()}
                      </div>
                    </div>
                  </div>
                  <div className="property-card-body">
                    <div style={{ marginBottom: '0.5rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Occupancy:</span>
                        <strong style={{ color: barColor }}>{occPct.toFixed(0)}%</strong>
                      </div>
                      <div style={{ background: '#e5e7eb', borderRadius: '9999px', height: '6px' }}>
                        <div style={{ width: `${occPct}%`, background: barColor, height: '6px', borderRadius: '9999px' }} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>Net Profit:</span>
                      <strong style={{ color: profit >= 0 ? '#15803d' : '#b91c1c' }}>{symbol}{profit.toFixed(2)}</strong>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* Upcoming Tasks */}
      {stats.upcomingTasks.length > 0 && (
        <Section id="tasks" title={`📅 Upcoming Tasks — next 30 days (${stats.upcomingTasks.length})`} defaultOpen={false}>
          <div className="card">
            <div className="table-container">
              <table className="table">
                <thead><tr><th>Task</th><th>Due Date</th><th>Priority</th></tr></thead>
                <tbody>
                  {stats.upcomingTasks.map(task => (
                    <tr key={task.id}>
                      <td>{task.title}</td>
                      <td>{new Date(task.due_date || task.date).toLocaleDateString('en-GB')}</td>
                      <td><span className={`badge ${task.priority === 'high' ? 'badge-danger' : task.priority === 'medium' ? 'badge-warning' : 'badge-success'}`}>{task.priority || 'low'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Section>
      )}
      {/* Quick Payment Modal */}
      {payModal && (
        <div className="modal-overlay" style={{cursor:'default'}} onClick={(e)=>{if(e.target===e.currentTarget)setPayModal(null);}}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">💰 Record Payment</span>
              <button className="modal-close" onClick={()=>setPayModal(null)}>×</button>
            </div>
            <div style={{marginBottom:'1rem',padding:'0.75rem',background:'var(--bg-light)',borderRadius:'0.5rem',fontSize:'0.875rem'}}>
              <strong>{payModal.propertyAddress}</strong><br/>
              {payModal.roomName} — {payModal.tenantName}<br/>
              <span style={{color:'var(--text-secondary)'}}>Rent due: {formatMoney(payModal.rentAmount, payModal.currency)}/mo</span>
            </div>
            {payError && <div className="alert alert-danger" style={{marginBottom:'1rem'}}>{payError}</div>}
            <form onSubmit={handleRecordPayment}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Month *</label>
                  <input type="month" value={payForm.month} onChange={e=>setPayForm(p=>({...p,month:e.target.value}))} required/>
                </div>
                <div className="form-group">
                  <label>Amount ({symbolFor(payModal.currency)}) *</label>
                  <input type="number" step="0.01" min="0.01" value={payForm.amount} onChange={e=>setPayForm(p=>({...p,amount:e.target.value}))} required/>
                </div>
                <div className="form-group">
                  <label>Date Received *</label>
                  <input type="date" value={payForm.paidDate} onChange={e=>setPayForm(p=>({...p,paidDate:e.target.value}))} required/>
                </div>
              </div>
              {/* Quick expenses */}
              <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:'0.5rem', padding:'0.75rem', marginBottom:'1rem' }}>
                <label style={{ display:'flex', alignItems:'center', gap:'0.5rem', cursor:'pointer', fontWeight:600, fontSize:'0.875rem', color:'#15803d' }}>
                  <input type="checkbox" checked={addExpenseAfterPayment} onChange={e=>setAddExpenseAfterPayment(e.target.checked)} style={{ width:'16px', height:'16px' }}/>
                  Also add expenses for this payment
                </label>
                {addExpenseAfterPayment && (
                  <div style={{ marginTop:'0.75rem' }}>
                    {quickExpenses.map((qe, i) => (
                      <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 100px 1fr auto', gap:'0.4rem', alignItems:'end', marginBottom:'0.5rem', paddingBottom:'0.5rem', borderBottom: i < quickExpenses.length-1 ? '1px solid #bbf7d0' : 'none' }}>
                        <div className="form-group" style={{ marginBottom:0 }}>
                          {i===0 && <label style={{ fontSize:'0.75rem' }}>Category</label>}
                          <select value={qe.category} onChange={e=>setQuickExpenseField(i,'category',e.target.value)}>
                            <option value="">Select...</option>
                            {EXPENSE_CATEGORIES.map(c=><option key={c}>{c}</option>)}
                          </select>
                        </div>
                        <div className="form-group" style={{ marginBottom:0 }}>
                          {i===0 && <label style={{ fontSize:'0.75rem' }}>Amount</label>}
                          <input type="number" step="0.01" value={qe.amount} onChange={e=>setQuickExpenseField(i,'amount',e.target.value)} placeholder="0.00"/>
                        </div>
                        <div className="form-group" style={{ marginBottom:0 }}>
                          {i===0 && <label style={{ fontSize:'0.75rem' }}>Description</label>}
                          <input value={qe.description} onChange={e=>setQuickExpenseField(i,'description',e.target.value)} placeholder="Optional notes"/>
                        </div>
                        <div style={{ display:'flex', gap:'0.3rem', alignItems:'center' }}>
                          <select value={qe.paid} onChange={e=>setQuickExpenseField(i,'paid',e.target.value==='true')} style={{ fontSize:'0.75rem', padding:'0.3rem' }}>
                            <option value="true">Paid</option>
                            <option value="false">Unpaid</option>
                          </select>
                          {quickExpenses.length > 1 && (
                            <button type="button" onClick={()=>removeQuickExpenseRow(i)} style={{ background:'none', border:'none', color:'#dc2626', cursor:'pointer', fontSize:'1rem', padding:'0 0.2rem' }}>×</button>
                          )}
                        </div>
                      </div>
                    ))}
                    <button type="button" onClick={addQuickExpenseRow} style={{ background:'none', border:'1px dashed #16a34a', color:'#16a34a', borderRadius:'0.375rem', padding:'0.3rem 0.75rem', cursor:'pointer', fontSize:'0.8rem', width:'100%', marginTop:'0.25rem' }}>
                      + Add another expense
                    </button>
                  </div>
                )}
              </div>
              <div style={{display:'flex',gap:'0.75rem',justifyContent:'flex-end'}}>
                <button type="button" className="btn btn-secondary" onClick={()=>{ setPayModal(null); setAddExpenseAfterPayment(false); setQuickExpenses([{ category:'', amount:'', description:'', paid:true }]); }}>Cancel</button>
                <button type="submit" className="btn btn-success" disabled={payLoading}>{payLoading?'Saving...':'✅ Record Payment'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
